import { clsx } from "clsx";
import type { MouseEvent } from "react";
import {
  IconCircle,
  IconCircleCheckFilled,
  IconHeart,
  IconHeartFilled,
} from "@tabler/icons-react";
import type { ProductDto } from "../../../../api";
import { Image } from "../../../../components/common/image";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { GatedPrice } from "../../../../components/common/gated-price";
import { useWishlistUiStore } from "../../../../hooks/stores/useWishlistUiStore";

export interface ProductListRowProps {
  product: ProductDto;
  discountLabel?: string;
  originalPrice?: number;
  stockLabel?: string;
  currency?: string;
  isInWishlist?: boolean;
  onToggleWishlist?: (e: MouseEvent) => void;
  onViewDetails?: () => void;
  onClick?: () => void;
  /** When true, renders the "Select" pill (compare mode). */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  /** When true (and not already selected), the Select pill is disabled — used when the compare list is full. */
  compareDisabled?: boolean;
  className?: string;
}

function stopPropagation(e: MouseEvent) {
  e.stopPropagation();
}

export function ProductListRow({
  product,
  discountLabel,
  originalPrice,
  stockLabel,
  currency = "QAR",
  isInWishlist = false,
  onToggleWishlist,
  onViewDetails,
  onClick,
  selectable = false,
  selected = false,
  onToggleSelect,
  compareDisabled = false,
  className,
}: ProductListRowProps) {

  const playFlyToWishlist = useWishlistUiStore((s) => s.playFlyToWishlist);
  const title = product.displayName || product.name || "—";
  const description = product.description;

  return (
    <article
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={clsx(
        "group relative flex w-full items-center gap-4 rounded-2xl border bg-bgWhite py-3 pl-3 pr-6 transition-colors",
        "shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)]",
        selected ? "border-brandPrimary" : "border-borderDefault",
        onClick && "cursor-pointer",
        className,
      )}
    >
      {/* Select (compare) toggle */}
      {selectable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.();
          }}
          aria-pressed={selected}
          disabled={!selected && compareDisabled}
          className={clsx(
            "absolute right-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-md border px-1.5 py-1 transition-colors",
            selected
              ? "border-brandPrimary bg-bgWhite"
              : "border-textMuted bg-bgMuted hover:bg-bgLight",
            !selected && compareDisabled && "cursor-not-allowed opacity-50 hover:bg-bgMuted",
          )}
        >
          <Text variant="bodySmBold" tone="body" inline>
            Select
          </Text>
          {selected ? (
            <IconCircleCheckFilled size={20} className="text-brandPrimary" />
          ) : (
            <IconCircle size={20} />
          )}
        </button>
      ) : null}

      {/* Image */}
      <div className="relative h-[120px] w-[174px] shrink-0 overflow-hidden rounded-lg bg-bgLight">
        <Image
          image={product.image}
          alt={title}
          size="w-full h-full"
          objectFit="cover"
          showPlaceholder
        />
        {onToggleWishlist && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isInWishlist) {
                const imageContainer = e.currentTarget.closest(".relative")?.querySelector(".w-full.h-full") as HTMLElement | null;
                playFlyToWishlist({
                  source: imageContainer ?? e.currentTarget,
                  image: product.image ?? undefined,
                });
              }
              onToggleWishlist(e);
            }}
            aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
            className="absolute left-2 top-2 z-10 flex size-7 items-center justify-center rounded-full bg-bgWhite shadow-sm transition-colors hover:bg-bgLight"
          >
            {isInWishlist
              ? <IconHeartFilled size={18} className="text-textBody" />
              : <IconHeart size={18} className="text-textMuted" />}
          </button>
        )}
      </div>

      {/* Info */}
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        <div className="flex w-full items-center gap-3">
          <Text variant="bodyBold" tone="dark" className="truncate">
            {title}
          </Text>
          {discountLabel ? (
            <span className="shrink-0"><Chip variant="secondary">{discountLabel}</Chip></span>
          ) : null}
        </div>

        {description ? (
          <Text variant="bodySm" tone="dark" className="line-clamp-2">
            {description}
          </Text>
        ) : null}


        <div className="flex w-full items-center gap-8">
          <CurrencyAmount
            amount={product.listPrice ?? 0}
            currency={currency}
            variant="bodyBold"
            tone="dark"
          />
          {originalPrice != null ? (
            <Text variant="bodySm" tone="body" inline className="line-through">
              <GatedPrice>{currency} {originalPrice.toLocaleString()}</GatedPrice>
            </Text>
          ) : null}
          {stockLabel ? (
            <Text variant="bodySm" inline className="text-accentAmber">
              {stockLabel}
            </Text>
          ) : null}
        </div>
      </div>

      {/* Actions */}
      <div className="ml-8 flex w-[240px] shrink-0 flex-col items-stretch gap-1.5">
        <Button
          variant="primary"
          fullWidth
          size="sm"
          onClick={(e) => {
            stopPropagation(e);
            onViewDetails?.();
          }}
        >
          See options
        </Button>
      </div>
    </article>
  );
}

export default ProductListRow;
